import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorCommand } from '@atlaskit/editor-common/types';
import { REMOVE_HIGHLIGHT_COLOR } from '@atlaskit/editor-common/ui-color';
import {
  BoldIcon,
  ClearFormattingIcon,
  CodeIcon,
  ItalicIcon,
  StrikeThroughIcon,
  SubscriptIcon,
  SuperscriptIcon,
  UnderlineIcon,
  ToolbarButton,
  ToolbarButtonGroup,
  ToolbarKeyboardShortcutHint,
  ToolbarSection,
  ToolbarTooltip,
} from '@atlaskit/editor-toolbar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { ButtonItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { type DatabasesEditorAPI } from '../databases-preset';

import {
  DATABASES_FORMATTING_TOOLBAR_POPUP_TEST_ID,
  POPUP_MENU_GROUP_WIDTH,
} from './toolbar-constants';
import { useToolbarShortcutKeys } from './use-toolbar-shortcut-keys';

interface TextFormattingSectionProps {
  editorApi: DatabasesEditorAPI;
  textFormattingState:
    | {
        strongActive?: boolean;
        strongDisabled?: boolean;
        emActive?: boolean;
        emDisabled?: boolean;
        underlineActive?: boolean;
        underlineDisabled?: boolean;
        strikeActive?: boolean;
        strikeDisabled?: boolean;
        codeActive?: boolean;
        codeDisabled?: boolean;
        subscriptActive?: boolean;
        subscriptDisabled?: boolean;
        superscriptActive?: boolean;
        superscriptDisabled?: boolean;
        formattingIsPresent?: boolean;
      }
    | undefined;
  textColorState: { color?: string | null; defaultColor?: string } | undefined;
  highlightState: { activeColor?: string | null } | undefined;
  execute: ((command: EditorCommand | undefined) => boolean) | undefined;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  preventBlur: React.MouseEventHandler;
}

export const TextFormattingSection: React.FC<TextFormattingSectionProps> = ({
  editorApi,
  textFormattingState,
  textColorState,
  highlightState,
  execute,
  isOpen,
  onToggle,
  onClose,
  preventBlur,
}) => {
  const { formatMessage } = useIntl();
  const { modHint, shiftHint } = useToolbarShortcutKeys();

  const toggleStrong = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleStrong(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleEm = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleEm(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleUnderline = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleUnderline(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleStrike = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleStrike(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleCode = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleCode(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleSubscript = useCallback(() => {
    execute?.(editorApi?.textFormatting?.commands.toggleSubscript(INPUT_METHOD.TOOLBAR));
  }, [execute, editorApi]);

  const toggleSuperscript = useCallback(() => {
    execute?.(
      editorApi?.textFormatting?.commands.toggleSuperscript(INPUT_METHOD.TOOLBAR),
    );
  }, [execute, editorApi]);

  const clearFormatting = useCallback(() => {
    const commands = editorApi?.textFormatting?.commands;
    if (!commands) {
      return;
    }
    if (textFormattingState?.strongActive) {
      execute?.(commands.toggleStrong(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.emActive) {
      execute?.(commands.toggleEm(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.underlineActive) {
      execute?.(commands.toggleUnderline(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.strikeActive) {
      execute?.(commands.toggleStrike(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.codeActive) {
      execute?.(commands.toggleCode(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.subscriptActive) {
      execute?.(commands.toggleSubscript(INPUT_METHOD.TOOLBAR));
    }
    if (textFormattingState?.superscriptActive) {
      execute?.(commands.toggleSuperscript(INPUT_METHOD.TOOLBAR));
    }
    const currentTextColor = textColorState?.color ?? textColorState?.defaultColor;
    const defaultTextColor = textColorState?.defaultColor;
    if (defaultTextColor && currentTextColor && currentTextColor !== defaultTextColor) {
      execute?.(
        editorApi?.textColor?.commands.changeColor(
          defaultTextColor,
          INPUT_METHOD.TOOLBAR,
        ),
      );
    }
    if (
      highlightState?.activeColor &&
      highlightState.activeColor !== REMOVE_HIGHLIGHT_COLOR
    ) {
      execute?.(
        editorApi?.highlight?.commands.changeColor({
          color: REMOVE_HIGHLIGHT_COLOR,
          inputMethod: INPUT_METHOD.TOOLBAR,
        }),
      );
    }
  }, [execute, editorApi, textFormattingState, textColorState, highlightState]);

  const hasFormattingToClear =
    textFormattingState?.formattingIsPresent ||
    (textColorState?.defaultColor &&
      textColorState?.color &&
      textColorState.color !== textColorState.defaultColor) ||
    (highlightState?.activeColor &&
      highlightState.activeColor !== REMOVE_HIGHLIGHT_COLOR);

  return (
    <ToolbarSection>
      <ToolbarButtonGroup>
        <ToolbarTooltip
          content={`${formatMessage(
            messages['rich-text-formatting-toolbar.bold'],
          )} ${modHint}B`}
          position="top"
        >
          <ToolbarButton
            iconBefore={
              <BoldIcon
                size="small"
                label={formatMessage(messages['rich-text-formatting-toolbar.bold'])}
              />
            }
            isSelected={textFormattingState?.strongActive}
            isDisabled={textFormattingState?.strongDisabled}
            onClick={toggleStrong}
            testId="bold-button"
          />
        </ToolbarTooltip>

        <Popup
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom-start"
          autoFocus={false}
          shouldDisableFocusLock
          trigger={(triggerProps) => (
            <ToolbarTooltip
              content={formatMessage(
                messages['rich-text-formatting-toolbar.format-text'],
              )}
              position="top"
            >
              <ToolbarButton
                {...triggerProps}
                iconBefore={<ChevronDownIcon label="" size="small" />}
                label={formatMessage(
                  messages['rich-text-formatting-toolbar.more-text-styles'],
                )}
                isSelected={isOpen}
                onClick={onToggle}
                testId="text-styles-dropdown-trigger"
              />
            </ToolbarTooltip>
          )}
          content={() => (
            <Box
              as="div"
              onMouseDown={preventBlur}
              testId={DATABASES_FORMATTING_TOOLBAR_POPUP_TEST_ID}
            >
              <PopupMenuGroup minWidth={POPUP_MENU_GROUP_WIDTH}>
                <Section>
                  <ButtonItem
                    iconBefore={
                      <BoldIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.bold'],
                        )}
                      />
                    }
                    iconAfter={<ToolbarKeyboardShortcutHint shortcut={`${modHint}B`} />}
                    isSelected={textFormattingState?.strongActive}
                    isDisabled={textFormattingState?.strongDisabled}
                    onClick={toggleStrong}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.bold'])}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <ItalicIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.italic'],
                        )}
                      />
                    }
                    iconAfter={<ToolbarKeyboardShortcutHint shortcut={`${modHint}I`} />}
                    isSelected={textFormattingState?.emActive}
                    isDisabled={textFormattingState?.emDisabled}
                    onClick={toggleEm}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.italic'])}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <UnderlineIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.underline'],
                        )}
                      />
                    }
                    iconAfter={<ToolbarKeyboardShortcutHint shortcut={`${modHint}U`} />}
                    isSelected={textFormattingState?.underlineActive}
                    isDisabled={textFormattingState?.underlineDisabled}
                    onClick={toggleUnderline}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.underline'])}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <StrikeThroughIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.strikethrough'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint}S`} />
                    }
                    isSelected={textFormattingState?.strikeActive}
                    isDisabled={textFormattingState?.strikeDisabled}
                    onClick={toggleStrike}
                  >
                    {formatMessage(
                      messages['rich-text-formatting-toolbar.strikethrough'],
                    )}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <CodeIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.code'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint}M`} />
                    }
                    isSelected={textFormattingState?.codeActive}
                    isDisabled={textFormattingState?.codeDisabled}
                    onClick={toggleCode}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.code'])}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <SubscriptIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.subscript'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint},`} />
                    }
                    isSelected={textFormattingState?.subscriptActive}
                    isDisabled={textFormattingState?.subscriptDisabled}
                    onClick={toggleSubscript}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.subscript'])}
                  </ButtonItem>
                  <ButtonItem
                    iconBefore={
                      <SuperscriptIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.superscript'],
                        )}
                      />
                    }
                    iconAfter={
                      <ToolbarKeyboardShortcutHint shortcut={`${modHint}${shiftHint}.`} />
                    }
                    isSelected={textFormattingState?.superscriptActive}
                    isDisabled={textFormattingState?.superscriptDisabled}
                    onClick={toggleSuperscript}
                  >
                    {formatMessage(messages['rich-text-formatting-toolbar.superscript'])}
                  </ButtonItem>
                </Section>
                <Section hasSeparator>
                  <ButtonItem
                    iconBefore={
                      <ClearFormattingIcon
                        label={formatMessage(
                          messages['rich-text-formatting-toolbar.clear-formatting'],
                        )}
                      />
                    }
                    iconAfter={<ToolbarKeyboardShortcutHint shortcut={`${modHint}\\`} />}
                    isDisabled={!hasFormattingToClear}
                    onClick={clearFormatting}
                  >
                    {formatMessage(
                      messages['rich-text-formatting-toolbar.clear-formatting'],
                    )}
                  </ButtonItem>
                </Section>
              </PopupMenuGroup>
            </Box>
          )}
        />
      </ToolbarButtonGroup>
    </ToolbarSection>
  );
};
